import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    OnDestroy,
    signal,
    Type,
    viewChildren,
} from '@angular/core';

import { MapModalEditionComponent } from '@connect-services/client/components/modals/map-modal/map-modal-edition/map-modal-edition.component';
import { MapModalFilterComponent } from '@connect-services/client/components/modals/map-modal/map-modal-filter/map-modal-filter.component';
import { MapModalMeasurementComponent } from '@connect-services/client/components/modals/map-modal/map-modal-measurement/map-modal-measurement.component';
import { MapModalSelectionComponent } from '@connect-services/client/components/modals/map-modal/map-modal-selection/map-modal-selection.component';
import { MapModalShapingComponent } from '@connect-services/client/components/modals/map-modal/map-modal-shaping/map-modal-shaping.component';
import { MapModalSnapComponent } from '@connect-services/client/components/modals/map-modal/map-modal-snap/map-modal-snap.component';
import { AssertService } from '@connect-services/client/services/assert.service';
import {
    ActiveElementEnum,
    MAP_ELEMENTS_DEFAULT_PADDING,
    MapElementsPositionService,
    Position,
} from '@connect-services/client/services/map-elements-position.service';
import {
    MapModal,
    MapModalService,
    ModalInstanceState,
    OpenedModalElement,
} from '@connect-services/client/services/map-modal.service';
import { MapPopupService } from '@connect-services/client/services/map-popup.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

const MARGIN_TOP_MODAL = 10;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-map-modal',
    styleUrl: './map-modal.component.scss',
    templateUrl: './map-modal.component.html',
})
export class MapModalComponent implements AfterViewInit, OnDestroy {
    protected readonly mapModalService = inject(MapModalService);
    protected readonly modalComponentByType: Record<MapModal, Type<unknown> | null> = {
        [MapModal.EDITION]: MapModalEditionComponent,
        [MapModal.FILTER]: MapModalFilterComponent,
        [MapModal.MEASUREMENT]: MapModalMeasurementComponent,
        [MapModal.NONE]: null,
        [MapModal.SELECTION]: MapModalSelectionComponent,
        [MapModal.SHAPING]: MapModalShapingComponent,
        [MapModal.SNAP]: MapModalSnapComponent,
    };
    protected readonly openedModals = computed((): MapModal[] => {
        return Object.values(MapModal).filter((modalType: MapModal): boolean => {
            return modalType !== MapModal.NONE && this.mapModalService.state()[modalType].isOpen;
        });
    });

    private readonly alreadyInitialized = new Set<MapModal>();
    private readonly dragAbortListenersByModalType = new Map<MapModal, AbortController>();
    private readonly dragOffsetByModalType = new Map<MapModal, Position>();
    private readonly isDraggingModalByType = signal<Record<MapModal, boolean>>({
        [MapModal.EDITION]: false,
        [MapModal.FILTER]: false,
        [MapModal.MEASUREMENT]: false,
        [MapModal.NONE]: false,
        [MapModal.SELECTION]: false,
        [MapModal.SHAPING]: false,
        [MapModal.SNAP]: false,
    });
    private readonly mapElementsPositionService = inject(MapElementsPositionService);
    private readonly mapPopupService = inject(MapPopupService);
    private readonly modalElementRefs = viewChildren<ElementRef<HTMLDivElement>>('modalElement');
    private readonly resizeObserverByModalType = new Map<MapModal, ResizeObserver>();

    public constructor() {
        effect(() => {
            const opened = this.openedModals();
            const newlyOpened: MapModal[] = [];

            for (const modalType of opened) {
                if (this.alreadyInitialized.has(modalType)) {
                    continue;
                }

                this.alreadyInitialized.add(modalType);
                newlyOpened.push(modalType);
            }

            Object.values(MapModal).forEach((type) => {
                if (type === MapModal.NONE || opened.includes(type)) {
                    return;
                }
                this.alreadyInitialized.delete(type);
            });

            if (!newlyOpened.length) {
                return;
            }

            queueMicrotask(() => {
                this.registerAllElements();

                let yOffset = 0;

                for (const modalType of newlyOpened) {
                    this.applyInitialPosition(modalType, yOffset);
                    this.observeResize(modalType);

                    const modalElement = this.mapModalService.getElement(modalType);
                    if (modalElement) {
                        yOffset += modalElement.getBoundingClientRect().height + MAP_ELEMENTS_DEFAULT_PADDING;
                    }
                    setTimeout(() => {
                        this.resolveAllModalsOverlap(modalType);
                    });
                }
            });
        });
    }

    public isDragging(type: MapModal): boolean {
        return this.isDraggingModalByType()[type];
    }

    public ngAfterViewInit(): void {
        this.registerAllElements();

        for (const modalType of this.openedModals()) {
            this.applyInitialPosition(modalType);

            this.constrainCurrentPosition(modalType);
            this.observeResize(modalType);
        }
    }

    public ngOnDestroy(): void {
        Object.values(MapModal).forEach((type: MapModal) => {
            this.mapModalService.unregisterElement(type);
            this.stopDragging(type);
            this.resizeObserverByModalType.get(type)?.disconnect();
        });
    }

    protected onPointerDown(event: PointerEvent, type: MapModal): void {
        const target = event.target as HTMLElement | null;

        if (!target || target.closest('button')) {
            return;
        }

        const modalElement: HTMLElement | null = this.mapModalService.getElement(type);
        if (!modalElement) {
            return;
        }

        const modalRect: DOMRect = modalElement.getBoundingClientRect();

        this.isDraggingModalByType.update((current) => ({
            ...current,
            [type]: true,
        }));

        this.dragOffsetByModalType.set(type, {
            x: event.clientX - modalRect.left,
            y: event.clientY - modalRect.top,
        });

        this.startDragging(type);

        event.preventDefault();
    }

    private applyConstrainedPosition(type: MapModal, position: Position): void {
        const constrainedPosition: Position | null = this.getConstrainedPosition(type, position);
        if (!constrainedPosition) {
            return;
        }

        this.mapModalService.moveTo(type, constrainedPosition);
        this.resolveAllModalsOverlap(type);
        this.mapPopupService.resolveOverlap(ActiveElementEnum.MODAL);
    }

    private applyInitialPosition(type: MapModal, yOffset: number = 0): void {
        const state: ModalInstanceState = this.mapModalService.state()[type];

        if (!state.isOpen || state.hasBeenMoved) {
            return;
        }

        const modalElement: HTMLElement | null = this.mapModalService.getElement(type);
        if (!modalElement) {
            return;
        }

        const containerElement: HTMLElement | null = modalElement.parentElement;
        if (!containerElement) {
            return;
        }

        const containerDimensions: DOMRect = containerElement.getBoundingClientRect();
        const modalDimensions: DOMRect = modalElement.getBoundingClientRect();

        const newPosition: Position = {
            x: containerDimensions.width - modalDimensions.width,
            y: MARGIN_TOP_MODAL + yOffset,
        };

        this.applyConstrainedPosition(type, newPosition);
    }

    private constrainCurrentPosition(type: MapModal): void {
        this.applyConstrainedPosition(type, this.mapModalService.state()[type].position);
    }

    private getConstrainedPosition(type: MapModal, position: Position): Position | null {
        const modalElement: HTMLElement | null = this.mapModalService.getElement(type);
        if (!modalElement) {
            return null;
        }

        const containerElement: HTMLElement | null = modalElement.parentElement;
        if (!containerElement) {
            return null;
        }

        const containerRect: DOMRect = containerElement.getBoundingClientRect();
        const modalRect: DOMRect = modalElement.getBoundingClientRect();

        return this.mapElementsPositionService.constrainPosition(
            position,
            containerRect,
            modalRect.width,
            modalRect.height,
        );
    }

    private getMaxModalIterations(): number {
        return Object.entries(this.modalComponentByType).filter((entry: [string, Type<unknown> | null]): boolean => {
            AssertService.isEnum<MapModal>(entry[0], MapModal);

            return entry[0] !== MapModal.NONE && entry[1] !== null;
        }).length;
    }

    private observeResize(type: MapModal): void {
        const modalElement: HTMLElement | null = this.mapModalService.getElement(type);
        if (!modalElement) {
            return;
        }

        const containerElement: HTMLElement | null = modalElement.parentElement;
        if (!containerElement) {
            return;
        }

        const observer = new ResizeObserver(() => this.onResize(type));
        observer.observe(modalElement);
        observer.observe(containerElement);

        this.resizeObserverByModalType.set(type, observer);
    }

    private onPointerMove(type: MapModal, event: PointerEvent): void {
        if (!this.isDragging(type)) {
            return;
        }

        const modalElement: HTMLElement | null = this.mapModalService.getElement(type);
        if (!modalElement) {
            return;
        }

        const containerElement: HTMLElement | null = modalElement.parentElement;
        if (!containerElement) {
            return;
        }

        const dragOffset: Position | undefined = this.dragOffsetByModalType.get(type);
        if (!dragOffset) {
            return;
        }

        const containerRect: DOMRect = containerElement.getBoundingClientRect();
        const newModalPosition: Position = {
            x: event.clientX - containerRect.left - dragOffset.x,
            y: event.clientY - containerRect.top - dragOffset.y,
        };

        this.applyConstrainedPosition(type, newModalPosition);
        this.mapModalService.markAsMoved(type);
    }

    private onPointerUp(type: MapModal): void {
        this.stopDragging(type);
    }

    private onResize(type: MapModal): void {
        this.constrainCurrentPosition(type);
    }

    private registerAllElements(): void {
        this.modalElementRefs().forEach((elementRef: ElementRef<HTMLDivElement>) => {
            const element: HTMLDivElement = elementRef.nativeElement;
            const type: string | undefined = element.dataset['modalType'];

            if (!type) {
                return;
            }

            AssertService.isEnum<MapModal>(type, MapModal);
            this.mapModalService.registerElement(type, element);
        });
    }

    private resolveAllModalsOverlap(activeType: MapModal): void {
        const activeElement: HTMLElement | null = this.mapModalService.getElement(activeType);
        if (!activeElement) {
            return;
        }

        const activeRect: DOMRect = activeElement.getBoundingClientRect();
        const maxActiveModalCount: number = this.getMaxModalIterations();

        for (let i = 0; i < maxActiveModalCount; i++) {
            const openedModals: OpenedModalElement[] = this.mapModalService.getOpenedElements();

            for (const passiveModal of openedModals) {
                if (passiveModal.type === activeType) {
                    continue;
                }

                const passiveRect: DOMRect = passiveModal.element.getBoundingClientRect();

                if (!this.mapElementsPositionService.areOverlapping(activeRect, passiveRect)) {
                    continue;
                }

                const passiveModalState: ModalInstanceState = this.mapModalService.state()[passiveModal.type];

                this.mapElementsPositionService.resolveModalsOverlap({
                    activeModalElement: activeElement,
                    activeModalPosition: this.mapModalService.state()[activeType].position,
                    padding: activeType === MapModal.SNAP ? 20 : undefined,
                    passiveModalAutoMove: (): void => this.mapModalService.setAutoMoving(true),
                    passiveModalElement: passiveModal.element,
                    passiveModalMove: (position: Position): void =>
                        this.mapModalService.moveTo(passiveModal.type, position),
                    passiveModalPosition: passiveModalState.position,
                });
            }
        }
    }

    private startDragging(type: MapModal): void {
        const controller = new AbortController();
        this.dragAbortListenersByModalType.set(type, controller);

        document.addEventListener('pointermove', (event: PointerEvent) => this.onPointerMove(type, event), {
            capture: true,
            signal: controller.signal,
        });

        document.addEventListener('pointerup', () => this.onPointerUp(type), {
            capture: true,
            signal: controller.signal,
        });

        document.addEventListener('pointercancel', () => this.onPointerUp(type), {
            capture: true,
            signal: controller.signal,
        });
    }

    private stopDragging(type: MapModal): void {
        this.isDraggingModalByType.update((current) => ({
            ...current,
            [type]: false,
        }));

        this.dragOffsetByModalType.delete(type);

        const controller: AbortController | undefined = this.dragAbortListenersByModalType.get(type);
        if (!controller) {
            return;
        }

        controller.abort();
        this.dragAbortListenersByModalType.delete(type);
    }
}
